import React from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 no-hover",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "text-text-primary hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                success: "bg-success text-success-foreground hover:bg-success/90",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90",
                danger: "bg-error text-error-foreground hover:bg-error/90",
            },
            size: {
                // All sizes now meet 44px minimum touch target requirements with standardized spacing
                default: "h-11 btn-spacing-md", // 44px height with medium spacing ✅
                sm: "h-11 rounded-md btn-spacing-sm text-sm", // 44px height with small spacing ✅
                lg: "h-12 rounded-md btn-spacing-lg", // 48px height with large spacing ✅
                icon: "h-11 w-11", // 44px x 44px ✅ (was h-10 w-10)
                xs: "h-11 rounded-md btn-spacing-xs text-xs", // 44px height with extra small spacing ✅
                xl: "h-12 rounded-md btn-spacing-xl text-base", // 48px height with extra large spacing ✅
                xxl: "h-14 rounded-md px-12 text-lg", // 56px height for emphasis
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(({
    className,
    variant,
    size,
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";

    // Icon size mapping based on button size
    const iconSizeMap = {
        xs: 12,
        sm: 14,
        default: 16,
        lg: 18,
        xl: 20,
        icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap[size] || 16;

    // Loading spinner - enhanced for accessibility
    const LoadingSpinner = () => (
        <svg 
            className="animate-spin -ml-1 mr-2 h-4 w-4" 
            fill="none" 
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // Icon rendering - enhanced for accessibility
    const renderIcon = () => {
        if (!iconName) return null;

        return (
            <Icon
                name={iconName}
                size={calculatedIconSize}
                className={cn(
                    children && iconPosition === 'left' && "mr-2",
                    children && iconPosition === 'right' && "ml-2"
                )}
                aria-hidden="true"
            />
        );
    };

    // Generate effective aria-label
    const getEffectiveAriaLabel = () => {
        if (ariaLabel) return ariaLabel;
        
        if (loading) {
            return `${children || 'Button'} - Loading`;
        }
        
        if (size === 'icon' && !children) {
            return iconName ? `${iconName} button` : 'Button';
        }
        
        return undefined;
    };

    return (
        <Comp
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full",
                "no-hover", // Force no hover effects on all buttons
                // Enhanced focus styles for accessibility
                "btn-focus",
                // Animation patterns
                "transition-all animate-normal animate-ease-out",
                "animate-hover-lift animate-click-scale"
            )}
            ref={ref}
            disabled={disabled || loading}
            aria-label={getEffectiveAriaLabel()}
            aria-describedby={ariaDescribedBy}
            aria-disabled={disabled || loading}
            type={!asChild ? (props.type || 'button') : undefined}
            {...props}
        >
            {asChild ? (
                <>
                    {loading && (
                        <>
                            <LoadingSpinner />
                            <span className="sr-only">Loading...</span>
                        </>
                    )}
                    {iconName && iconPosition === 'left' && renderIcon()}
                    {children}
                    {iconName && iconPosition === 'right' && renderIcon()}
                </>
            ) : (
                <>
                    {loading && (
                        <>
                            <LoadingSpinner />
                            <span className="sr-only">Loading...</span>
                        </>
                    )}
                    {iconName && iconPosition === 'left' && renderIcon()}
                    {children}
                    {iconName && iconPosition === 'right' && renderIcon()}
                </>
            )}
        </Comp>
    );
});

Button.displayName = "Button";

export default Button;